import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { BaseComponent } from '@macs-frontend/core';
import { ListsUrls } from '@macs-frontend/web-macs/core';
import { Store } from '@ngrx/store';
import { FormControlState, FormGroupState, SetValueAction } from 'ngrx-forms';
import { Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { dateValueConverter, Language, Timezone } from '../../';
import { environment as env } from '../../../../../environments/environment';
import * as UsersActions from '../../../company-management/manage/sections/users/state/users.actions';
import { WizardTabs } from '../../enums/wizard-tabs.enum';
import { Tab } from '../../models/tab.model';
import { User, UserBillableRateApplyPeriods, UserForm } from '../../models/user.model';
import { ModalComponent } from '../modal/modal.component';
import { TaskRoleStatuses } from '@macs-frontend/features/shared/models/task-role.model';
import { OccupationTypeStatus } from '@macs-frontend/features/company-management/agenda/agenda.types';

enum ErrorMessages {
  EmailAlreadyExists = 'Sorry, but this email already exists',
  IncorrectFormat = 'Sorry, but this email is invalid',
}

const REASON_BILLABLE_RATE_SAVED = 'REASON_BILLABLE_RATE_SAVED';

@Component({
  /* eslint-disable-next-line @angular-eslint/component-selector */
  selector: 'md-edit-user-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './edit-user-form.component.html',
  styles: [
    `
      ::ng-deep .ngrx-forms-invalid.ngrx-forms-dirty:not(form) input {
        border-color: #ff7275;
      }
      input.ngrx-forms-dirty.ngrx-forms-invalid {
        border: 1px solid #f00;
      }
    `,
  ],
})
export class UserDetailsFormComponent extends BaseComponent {
  // @ts-expect-error
  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild('billableRateConfirmModal')
  billableRateConfirmModal?: ModalComponent;

  @Input() user?: User | null;
  // @ts-expect-error
  @Input() formState: FormGroupState<UserForm>;
  @Input() showValidationError = false;
  // @ts-expect-error
  @Input() currentTab: Tab;
  @Input() languages?: Language[] | null;
  @Input() timezones?: Timezone[] | null;

  @Output() picChangeHandler: EventEmitter<File> = new EventEmitter<File>();
  @Output()
  selectTitleHandler: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  removeEmailInputHandler: EventEmitter<number> = new EventEmitter<number>();
  @Output() addEmailInputHandler: EventEmitter<void> = new EventEmitter<void>();
  @Output() addPhoneInputHandler: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  removePhoneInputHandler: EventEmitter<number> = new EventEmitter<number>();
  @Output()
  validateUsernameHandler: EventEmitter<FormControlState<string>> = new EventEmitter<
    FormControlState<string>
  >();

  imagesPath = env.images_path;
  tabsTypes = WizardTabs;
  dateValueConverter = dateValueConverter;
  capacityArray: {
    name: string;
    capacity: number;
  }[] = Array.from({ length: 120 }, (_, i) => {
    // eslint-disable-next-line no-param-reassign
    const capacity = ++i / 2;
    return {
      capacity,
      name: `${capacity} ${capacity === 35 ? '(Default)' : ''}`,
    };
  }).reverse();
  ErrorMessages = ErrorMessages;
  UserBillableRateApplyPeriods = UserBillableRateApplyPeriods;
  // @ts-expect-error
  lastConfirmedBillableRate: string;

  billableRateConfirmResult = new Subject<boolean>();

  onPicChange(files: FileList) {
    const selectedPhoto = files.item(0);
    if (selectedPhoto) {
      this.picChangeHandler.emit(selectedPhoto);
    }
  }

  openFileDialog(): void {
    const event: MouseEvent = new MouseEvent('click', { bubbles: false });
    this.fileInput.nativeElement.dispatchEvent(event);
  }

  selectTitle(title: string) {
    this.selectTitleHandler.emit(title);
  }

  removeEmailInput(index: number) {
    this.removeEmailInputHandler.emit(index);
  }

  removePhoneInput(index: number) {
    this.removePhoneInputHandler.emit(index);
  }

  addEmailInput() {
    this.addEmailInputHandler.emit();
  }

  addPhoneInput() {
    this.addPhoneInputHandler.emit();
  }

  onMainEmailChange(control: FormControlState<string>) {
    if (control.value.trim() !== this.user?.username) {
      this.validateUsernameHandler.emit(control);
    }
  }

  openDatepicker(datePicker: MatDatepicker<ElementRef>) {
    datePicker.open();
  }

  isEmailInvalid() {
    return (
      (this.formState.controls.emails.controls[0].controls.email.isInvalid &&
        this.formState.controls.emails.controls[0].controls.email.isDirty) ||
      (this.formState.controls.emails.controls[0].controls.email.isInvalid &&
        this.showValidationError)
    );
  }

  confirmBillableRate(): Observable<boolean> {
    const value = this.formState.value.billableRate.rate;
    if (
      this.user?.uuid &&
      this.user.hasActiveTimeTracks &&
      value !== this.lastConfirmedBillableRate &&
      value !== (this.user.currentBillableRate || '').toString()
    ) {
      this.billableRateConfirmModal?.open();
    } else {
      setTimeout(() => this.billableRateConfirmResult.next(true));
    }

    return this.billableRateConfirmResult.pipe(
      take(1),
      filter(confirmResult => confirmResult),
    );
  }

  resetBillableRate(modalCloseReason?: string) {
    if (modalCloseReason !== REASON_BILLABLE_RATE_SAVED) {
      this.store$.dispatch(
        new SetValueAction(
          // @ts-expect-error
          this.formState.controls.billableRate.controls.rate.id,
          this.lastConfirmedBillableRate || this.user?.currentBillableRate.toString(),
        ),
      );
    }
    this.billableRateConfirmResult.next(false);
  }

  saveBillableRate(modal: ModalComponent) {
    modal.close(REASON_BILLABLE_RATE_SAVED);
    // @ts-expect-error
    this.lastConfirmedBillableRate = this.formState.value.billableRate.rate;
    this.billableRateConfirmResult.next(true);
  }

  generateFuelCode() {
    if (this.user?.uuid) {
      this.store$.dispatch(new UsersActions.GenerateTankCode(this.user.uuid));
    }
  }

  mainPhoneChange() {
    this.store$.dispatch(new UsersActions.MainPhoneChange());
  }

  constructor(readonly store$: Store<any>) {
    super();
  }

  protected readonly listsUrls = ListsUrls;
  protected readonly TaskRoleStatuses = TaskRoleStatuses;
  protected readonly OccupationTypeStatus = OccupationTypeStatus;
}
